/* eslint-disable max-depth */
/* eslint-disable complexity */
/* eslint-disable max-len */
/* eslint-disable object-curly-newline, no-underscore-dangle, camelcase, no-unneeded-ternary */
const EventEmitter = require('../utils/events');
const helpers = require('../utils/helpers');
const RequestQueue = require('../utils/request-queue');

const MAX_URL_LENGTH = 2048;

function applyParams(parameters, options) {
  const {
    apiKey,
    version,
    sessionId,
    clientId,
    userId,
    segments,
    testCells,
    requestMethod,
    beaconMode,
  } = options;
  const { host, pathname, search } = helpers.getWindowLocation();
  const canonicalUrl = helpers.getCanonicalUrl();
  const documentReferrer = helpers.getDocumentReferrer();
  const sendReferrerWithTrackingEvents = (options.sendReferrerWithTrackingEvents === false)
    ? false
    : true; // Defaults to 'true'
  let aggregateParams = Object.assign(parameters);

  if (version) {
    aggregateParams.c = version;
  }

  if (clientId) {
    aggregateParams.i = clientId;
  }

  if (sessionId) {
    aggregateParams.s = sessionId;
  }

  if (userId) {
    aggregateParams.ui = String(userId);
  }

  if (segments && segments.length) {
    aggregateParams.us = segments;
  }

  if (apiKey) {
    aggregateParams.key = apiKey;
  }

  if (documentReferrer) {
    aggregateParams.document_referrer = documentReferrer;
  }

  if (canonicalUrl) {
    aggregateParams.canonical_url = canonicalUrl;
  }

  if (testCells) {
    Object.keys(testCells).forEach((testCellKey) => {
      aggregateParams[`ef-${testCellKey}`] = testCells[testCellKey];
    });
  }

  if (beaconMode && requestMethod && requestMethod.match(/POST/i)) {
    aggregateParams.beacon = true;
  }

  if (sendReferrerWithTrackingEvents && host) {
    aggregateParams.origin_referrer = host;

    if (pathname) {
      aggregateParams.origin_referrer += pathname;
    }

    if (search) {
      try {
        const utmQueryParamStrArr = [];
        const searchParams = new URLSearchParams(search);

        searchParams.forEach((value, key) => {
          if (key.match(/utm_/)) {
            utmQueryParamStrArr.push(`${key}=${value}`);
          }
        });

        if (utmQueryParamStrArr.length) {
          aggregateParams.origin_referrer += `?${utmQueryParamStrArr.join('&')}`;
        }
      } catch (e) {
        // Do nothing
      }
    }
  }

  aggregateParams._dt = Date.now();
  aggregateParams = helpers.cleanParams(aggregateParams);

  return aggregateParams;
}

// Append common parameters to supplied parameters object and return as string
function applyParamsAsString(parameters, options) {
  return helpers.stringify(applyParams(parameters, options));
}

/**
 * Interface to tracking related API calls
 *
 * @module tracker
 * @inner
 * @returns {object}
 */
class Tracker {
  constructor(options) {
    this.options = options || {};
    this.eventemitter = new EventEmitter();
    this.requests = new RequestQueue(options, this.eventemitter);
  }

  /**
   * Send session start event to API
   * @private
   * @function trackSessionStartV2
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @example
   * constructorio.tracker.trackSessionStartV2();
   */
  trackSessionStartV2(networkParameters = {}) {
    const url = `${this.options.serviceUrl}/v2/behavioral_action/session_start?`;

    this.requests.queue(`${url}${applyParamsAsString({}, this.options)}`, 'POST', undefined, networkParameters);
    this.requests.send();

    return true;
  }

  /**
   * Send session start event to API
   *
   * @function trackSessionStart
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @example
   * constructorio.tracker.trackSessionStart();
   */
  trackSessionStart(networkParameters = {}) {
    const url = `${this.options.serviceUrl}/behavior?`;
    const queryParams = { action: 'session_start' };

    this.requests.queue(`${url}${applyParamsAsString(queryParams, this.options)}`, undefined, undefined, networkParameters);
    this.requests.send();

    return true;
  }

  /**
   * Send input focus event to API
   * @private
   * @function trackInputFocusV2
   * @param {string} userInput - The current autocomplete search query
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User focused on search input element
   * @example
   * constructorio.tracker.trackInputFocusV2("text");
   */
  trackInputFocusV2(userInput = '', parameters = {}, networkParameters = {}) {
    const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/focus?`;
    const bodyParams = {};
    const {
      analyticsTags = null,
    } = parameters;

    if (analyticsTags) {
      bodyParams.analytics_tags = analyticsTags;
    }

    bodyParams.user_input = userInput;

    const requestMethod = 'POST';
    const requestBody = applyParams(bodyParams, {
      ...this.options,
      requestMethod,
    });
    this.requests.queue(`${baseUrl}${applyParamsAsString({}, this.options)}`, requestMethod, requestBody, networkParameters);
    this.requests.send();

    return true;
  }

  /**
   * Send input focus event to API
   *
   * @function trackInputFocus
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User focused on search input element
   * @example
   * constructorio.tracker.trackInputFocus();
   */
  trackInputFocus(networkParameters = {}) {
    const url = `${this.options.serviceUrl}/behavior?`;
    const queryParams = { action: 'focus' };

    this.requests.queue(`${url}${applyParamsAsString(queryParams, this.options)}`, undefined, undefined, networkParameters);
    this.requests.send();

    return true;
  }

  /**
   * Send item detail load event to API
   *
   * @function trackItemDetailLoad
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemName - Product item name
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} parameters.url - Current page URL
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User loaded an item detail page
   * @example
   * constructorio.tracker.trackItemDetailLoad(
   *     {
   *         itemName: 'Red T-Shirt',
   *         itemId: 'KMH876',
   *         url: 'https://constructor.io/product/KMH876',
   *     },
   * );
   */
  trackItemDetailLoad(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestUrlPath = `${this.options.serviceUrl}/v2/behavioral_action/item_detail_load?`;
      const queryParams = {};
      const bodyParams = {};
      const {
        item_name,
        name,
        item_id,
        customer_id,
        customerId = customer_id,
        variation_id,
        itemName = item_name || name,
        itemId = item_id || customerId,
        variationId = variation_id,
        url,
        analyticsTags,
      } = parameters;

      // Ensure support for both item_name and name as parameters
      if (itemName) {
        bodyParams.item_name = itemName;
      }

      // Ensure support for both item_id and customer_id as parameters
      if (itemId) {
        bodyParams.item_id = itemId;
      }

      if (variationId) {
        bodyParams.variation_id = variationId;
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      if (url) {
        bodyParams.url = helpers.truncateString(url, MAX_URL_LENGTH);
      }

      const requestURL = `${requestUrlPath}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(requestURL, requestMethod, requestBody, networkParameters);
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send autocomplete select event to API
   * @private
   * @function trackAutocompleteSelectV2
   * @param {string} itemName - Name of selected autocomplete item
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.userInput - The current autocomplete search query
   * @param {string} [parameters.section] - Section the selected item resides within
   * @param {string} [parameters.tr] - Trigger used to select the item (click, etc.)
   * @param {string} [parameters.itemId] - Item id of the selected item
   * @param {string} [parameters.variationId] - Variation id of the selected item
   * @param {string} [parameters.groupId] - Group identifier of the group to search within. Only required if searching within a group, i.e. "Pumpkin in Canned Goods"
   * @param {string} [parameters.slCampaignId] - Pass campaign id of sponsored listing
   * @param {string} [parameters.slCampaignOwner] - Pass campaign owner of sponsored listing
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User selected (clicked, or navigated to via keyboard) a result that appeared within autocomplete
   * @example
   * constructorio.tracker.trackAutocompleteSelectV2(
   *     'T-Shirt',
   *      {
   *          userInput: 'Shirt',
   *          section: 'Products',
   *          tr: 'click',
   *          groupId: '88JU230',
   *          itemId: '12345',
   *          variationId: '12345-A',
   *          slCampaignId: 'Campaign 123',
   *          slCampaignOwner: 'Store 123',
   *      },
   * );
   */
  trackAutocompleteSelectV2(itemName, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (itemName && typeof itemName === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/autocomplete_select?`;
        const {
          original_query,
          originalQuery = original_query,
          user_input,
          userInput = originalQuery || user_input,
          original_section,
          section = original_section,
          tr,
          group_id,
          groupId = group_id,
          item_id,
          itemId = item_id,
          variation_id,
          variationId = variation_id,
          slCampaignId,
          slCampaignOwner,
        } = parameters;
        const queryParams = {};
        const bodyParams = {
          user_input: userInput,
          tr,
          group_id: groupId,
          item_id: itemId,
          variation_id: variationId,
          item_name: itemName,
          section,
        };

        if (section) {
          queryParams.section = section;
        }

        if (slCampaignId) {
          bodyParams.sl_campaign_id = slCampaignId;
        }

        if (slCampaignOwner) {
          bodyParams.sl_campaign_owner = slCampaignOwner;
        }

        const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
        const requestMethod = 'POST';
        const requestBody = applyParams(bodyParams, {
          ...this.options,
          requestMethod,
        });
        this.requests.queue(
          requestURL,
          requestMethod,
          requestBody,
          networkParameters,
        );
        this.requests.send();

        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send autocomplete select event to API
   *
   * @function trackAutocompleteSelect
   * @param {string} term - Term of selected autocomplete item
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.originalQuery - The current autocomplete search query
   * @param {string} parameters.section - Section the selected item resides within
   * @param {string} [parameters.tr] - Trigger used to select the item (click, etc.)
   * @param {string} [parameters.groupId] - Group identifier of the group to search within. Only required if searching within a group, i.e. "Pumpkin in Canned Goods"
   * @param {string} [parameters.displayName] - Display name of group of selected item
   * @param {string} [parameters.itemId] - Item id of the selected item
   * @param {string} [parameters.slCampaignId] - Pass campaign id of sponsored listing
   * @param {string} [parameters.slCampaignOwner] - Pass campaign owner of sponsored listing
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User selected (clicked, or navigated to via keyboard) a result that appeared within autocomplete
   * @example
   * constructorio.tracker.trackAutocompleteSelect(
   *     'T-Shirt',
   *      {
   *         originalQuery: 'Shirt',
   *         section: 'Products',
   *         tr: 'click',
   *         groupId: '88JU230',
   *         displayName: 'apparel',
   *         itemId: '12345',
   *         slCampaignId: 'Campaign 123',
   *         slCampaignOwner: 'Store 123',
   *      },
   * );
   */
  trackAutocompleteSelect(term, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (term && typeof term === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const url = `${this.options.serviceUrl}/autocomplete/${helpers.encodeURIComponentRFC3986(helpers.trimNonBreakingSpaces(term))}/select?`;
        const queryParams = {};
        const {
          original_query,
          originalQuery = original_query,
          section,
          original_section,
          originalSection = original_section,
          tr,
          group_id,
          groupId = group_id,
          display_name,
          displayName = display_name,
          item_id,
          itemId = item_id,
          slCampaignOwner,
          slCampaignId,
        } = parameters;

        if (originalQuery) {
          queryParams.original_query = originalQuery;
        }

        if (tr) {
          queryParams.tr = tr;
        }

        if (originalSection || section) {
          queryParams.section = originalSection || section;
        }

        if (groupId) {
          queryParams.group = {
            group_id: groupId,
            display_name: displayName,
          };
        }

        if (itemId) {
          queryParams.item_id = itemId;
        }

        if (slCampaignId) {
          queryParams.sl_campaign_id = slCampaignId;
        }

        if (slCampaignOwner) {
          queryParams.sl_campaign_owner = slCampaignOwner;
        }

        this.requests.queue(`${url}${applyParamsAsString(queryParams, this.options)}`, undefined, undefined, networkParameters);
        this.requests.send();

        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send autocomplete search event to API
   * @private
   * @function trackSearchSubmitV2
   * @param {string} searchTerm - Term of submitted autocomplete event
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.userInput - The current autocomplete search query
   * @param {string} [parameters.groupId] - Group identifier of the group to search within. Only required if searching within a group, i.e. "Pumpkin in Canned Goods"
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted a search (pressing enter within input element, or clicking submit element)
   * @example
   * constructorio.tracker.trackSearchSubmitV2(
   *     'T-Shirt',
   *     {
   *         userInput: 'Shirt',
   *         groupId: '88JU230',
   *     },
   * );
   */
  trackSearchSubmitV2(searchTerm, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (searchTerm && typeof searchTerm === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/search_submit?`;
        const {
          original_query,
          originalQuery = original_query,
          user_input,
          userInput = originalQuery || user_input,
          group_id,
          groupId = group_id,
          section,
          analyticsTags = null,
        } = parameters;
        const queryParams = {};
        const bodyParams = {
          user_input: userInput,
          search_term: searchTerm,
          section,
        };

        if (groupId) {
          bodyParams.filters = { group_id: groupId };
        }

        if (section) {
          queryParams.section = section;
        }

        if (analyticsTags) {
          bodyParams.analytics_tags = analyticsTags;
        }

        const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
        const requestMethod = 'POST';
        const requestBody = applyParams(bodyParams, {
          ...this.options,
          requestMethod,
        });
        this.requests.queue(
          requestURL,
          requestMethod,
          requestBody,
          networkParameters,
        );
        this.requests.send();
        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send autocomplete search event to API
   *
   * @function trackSearchSubmit
   * @param {string} term - Term of submitted autocomplete event
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.originalQuery - The current autocomplete search query
   * @param {string} [parameters.groupId] - Group identifier of the group to search within. Only required if searching within a group, i.e. "Pumpkin in Canned Goods"
   * @param {string} [parameters.displayName] - Display name of group of selected item
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted a search (pressing enter within input element, or clicking submit element)
   * @example
   * constructorio.tracker.trackSearchSubmit(
   *     'T-Shirt',
   *     {
   *         originalQuery: 'Shirt',
   *         groupId: '88JU230',
   *         displayName: 'apparel',
   *     },
   * );
   */
  trackSearchSubmit(term, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (term && typeof term === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const url = `${this.options.serviceUrl}/autocomplete/${helpers.encodeURIComponentRFC3986(helpers.trimNonBreakingSpaces(term))}/search?`;
        const queryParams = {};
        const {
          original_query,
          originalQuery = original_query,
          group_id,
          groupId = group_id,
          display_name,
          displayName = display_name,
          analyticsTags,
        } = parameters;

        if (originalQuery) {
          queryParams.original_query = originalQuery;
        }

        if (groupId) {
          queryParams.group = {
            group_id: groupId,
            display_name: displayName,
          };
        }

        if (analyticsTags) {
          queryParams.analytics_tags = analyticsTags;
        }

        this.requests.queue(`${url}${applyParamsAsString(queryParams, this.options)}`, undefined, undefined, networkParameters);
        this.requests.send();

        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send search results loaded event to API
   *
   * @function trackSearchResultsLoaded
   * @param {string} searchTerm - Search results query term
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.url - URL of the search results page
   * @param {object[]} parameters.items - List of product item unique identifiers in search results listing
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - Current page of search results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {string} [parameters.resultId] - Browse result identifier (returned in response from Constructor)
   * @param {object} [parameters.selectedFilters] - Selected filters
   * @param {string} [parameters.sortOrder] - Sort order ('ascending' or 'descending')
   * @param {string} [parameters.sortBy] - Sorting method
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a search product listing page
   * @example
   * constructorio.tracker.trackSearchResultsLoaded(
   *     'T-Shirt',
   *     {
   *         resultCount: 167,
   *         items: [{itemId: 'KMH876'}, {itemId: 'KMH140'}, {itemId: 'KMH437'}],
   *         sortOrder: 'ascending'
   *         sortBy: 'price',
   *         resultPage: 3,
   *         resultCount: 20
   *     },
   * );
   */
  trackSearchResultsLoaded(searchTerm, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (searchTerm && typeof searchTerm === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/search_result_load?`;
        const {
          num_results,
          numResults = num_results,
          result_count,
          customerIds,
          customer_ids = customerIds,
          itemIds,
          item_ids = itemIds,
          items = customer_ids || item_ids,
          result_page,
          resultPage = result_page,
          result_offset,
          resultOffset = result_offset,
          result_id,
          resultId = result_id,
          sort_order,
          sortOrder = sort_order,
          sort_by,
          sortBy = sort_by,
          selected_filters,
          selectedFilters = selected_filters,
          url = helpers.getWindowLocation()?.href || 'N/A',
          section,
          analyticsTags,
          resultCount = numResults ?? result_count ?? items?.length ?? 0,
        } = parameters;
        const queryParams = {};
        let transformedItems;

        if (items && Array.isArray(items) && items.length !== 0) {
          if (typeof items[0] === 'string' || typeof items[0] === 'number') {
            transformedItems = items.map((itemId) => ({ item_id: String(itemId) }));
          } else {
            transformedItems = items.map((item) => helpers.toSnakeCaseKeys(item, false));
          }
        }

        if (section) {
          queryParams.section = section;
        }

        const bodyParams = {
          search_term: searchTerm,
          result_count: resultCount,
          items: transformedItems,
          result_page: resultPage,
          result_offset: resultOffset,
          result_id: resultId,
          sort_order: sortOrder,
          sort_by: sortBy,
          selected_filters: selectedFilters,
          url: helpers.truncateString(url, MAX_URL_LENGTH),
          section,
          analytics_tags: analyticsTags,
        };

        const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
        const requestMethod = 'POST';
        const requestBody = applyParams(bodyParams, {
          ...this.options,
          requestMethod,
        });
        this.requests.queue(
          requestURL,
          requestMethod,
          requestBody,
          networkParameters,
        );
        this.requests.send();
        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send click through event to API
   * @private
   * @function trackSearchResultClickV2
   * @param {string} searchTerm - Search results query term
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemName - Product item name (Either itemName or itemId is required)
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.resultId] - Search result identifier (returned in response from Constructor)
   * @param {number} [parameters.resultCount] - Number of results in total
   * @param {number} [parameters.resultPage] - Current page of results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {string} [parameters.resultPositionOnPage] - Position of selected items on page
   * @param {string} [parameters.numResultsPerPage] - Number of results per page
   * @param {object} [parameters.selectedFilters] - Key - Value map of selected filters
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {string} [parameters.slCampaignId] - Pass campaign id of sponsored listing
   * @param {string} [parameters.slCampaignOwner] - Pass campaign owner of sponsored listing
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a result that appeared within a search product listing page
   * @example
   * constructorio.tracker.trackSearchResultClickV2(
   *     'T-Shirt',
   *     {
   *         itemName: 'Red T-Shirt',
   *         itemId: 'KMH876',
   *         resultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         slCampaignId: 'Campaign 123',
   *         slCampaignOwner: 'Store 123',
   *     },
   * );
   */
  trackSearchResultClickV2(searchTerm, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (searchTerm && typeof searchTerm === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/search_result_click?`;
        const {
          num_results,
          customer_id,
          item_id,
          itemId = customer_id || item_id,
          name,
          item_name,
          itemName = name || item_name,
          variation_id,
          variationId = variation_id,
          result_id,
          resultId = result_id,
          result_count,
          resultCount = num_results || result_count,
          result_page,
          resultPage = result_page,
          result_offset,
          resultOffset = result_offset,
          result_position_on_page,
          resultPositionOnPage = result_position_on_page,
          num_results_per_page,
          numResultsPerPage = num_results_per_page,
          selected_filters,
          selectedFilters = selected_filters,
          section,
          analyticsTags,
          slCampaignId,
          slCampaignOwner,
        } = parameters;
        const bodyParams = {
          sl_campaign_id: slCampaignId,
          sl_campaign_owner: slCampaignOwner,
          item_name: itemName,
          item_id: itemId,
          variation_id: variationId,
          result_id: resultId,
          result_count: resultCount,
          result_page: resultPage,
          result_offset: resultOffset,
          result_position_on_page: resultPositionOnPage,
          num_results_per_page: numResultsPerPage,
          selected_filters: selectedFilters,
          section,
          search_term: searchTerm,
          analytics_tags: analyticsTags,
        };
        const queryParams = {};

        if (section) {
          queryParams.section = section;
        }

        const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
        const requestMethod = 'POST';
        const requestBody = applyParams(bodyParams, {
          ...this.options,
          requestMethod,
        });
        this.requests.queue(
          requestURL,
          requestMethod,
          requestBody,
          networkParameters,
        );
        this.requests.send();
        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send click through event to API
   *
   * @function trackSearchResultClick
   * @param {string} term - Search results query term
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} [parameters.itemName] - Product item name
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.resultId] - Search result identifier (returned in response from Constructor)
   * @param {string} [parameters.itemIsConvertible] - Whether or not an item is available for a conversion
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {string} [parameters.slCampaignId] - Pass campaign id of sponsored listing
   * @param {string} [parameters.slCampaignOwner] - Pass campaign owner of sponsored listing
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a result that appeared within a search product listing page
   * @example
   * constructorio.tracker.trackSearchResultClick(
   *     'T-Shirt',
   *     {
   *         itemName: 'Red T-Shirt',
   *         itemId: 'KMH876',
   *         resultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         slCampaignId: 'Campaign 123',
   *         slCampaignOwner: 'Store 123',
   *     },
   * );
   */
  trackSearchResultClick(term, parameters, networkParameters = {}) {
    // Ensure term is provided (required)
    if (term && typeof term === 'string') {
      // Ensure parameters are provided (required)
      if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
        const url = `${this.options.serviceUrl}/autocomplete/${helpers.encodeURIComponentRFC3986(helpers.trimNonBreakingSpaces(term))}/click_through?`;
        const queryParams = {};
        const {
          item_name,
          name,
          itemName = item_name || name,
          item_id,
          itemId = item_id,
          customer_id,
          customerId = customer_id || itemId,
          variation_id,
          variationId = variation_id,
          result_id,
          resultId = result_id,
          item_is_convertible,
          itemIsConvertible = item_is_convertible,
          section,
          analyticsTags,
          slCampaignOwner,
          slCampaignId,
        } = parameters;

        // Ensure support for both item_name and name as parameters
        if (itemName) {
          queryParams.name = itemName;
        }

        // Ensure support for both item_id and customer_id as parameters
        if (customerId) {
          queryParams.customer_id = customerId;
        }

        if (variationId) {
          queryParams.variation_id = variationId;
        }

        if (resultId) {
          queryParams.result_id = resultId;
        }

        if (typeof itemIsConvertible === 'boolean') {
          queryParams.item_is_convertible = itemIsConvertible;
        }

        if (section) {
          queryParams.section = section;
        }

        if (analyticsTags) {
          queryParams.analytics_tags = analyticsTags;
        }

        if (slCampaignId) {
          queryParams.sl_campaign_id = slCampaignId;
        }

        if (slCampaignOwner) {
          queryParams.sl_campaign_owner = slCampaignOwner;
        }

        this.requests.queue(`${url}${applyParamsAsString(queryParams, this.options)}`, undefined, undefined, networkParameters);
        this.requests.send();

        return true;
      }

      this.requests.send();

      return new Error('parameters are required of type object');
    }

    this.requests.send();

    return new Error('term is a required parameter of type string');
  }

  /**
   * Send conversion event to API
   *
   * @function trackConversion
   * @param {string} [term] - Search results query term that led to conversion event
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {number} [parameters.revenue] - Sale price if available, otherwise the regular (retail) price of item
   * @param {string} [parameters.itemName] - Product item name
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.type='add_to_cart'] - Conversion type
   * @param {boolean} [parameters.isCustomType] - Specify if type is custom conversion type
   * @param {string} [parameters.displayName] - Display name for the custom conversion type
   * @param {string} [parameters.section="Products"] - Index section
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User performed an action indicating interest in an item (add to cart, add to wishlist, etc.)
   * @see https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking
   * @example
   * constructorio.tracker.trackConversion(
   *     'T-Shirt',
   *     {
   *         itemId: 'KMH876',
   *         revenue: 12.00,
   *         itemName: 'Red T-Shirt',
   *         variationId: 'KMH879-7632',
   *         type: 'like',
   *         section: 'Products',
   *     },
   * );
   */
  trackConversion(term, parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const searchTerm = term || 'TERM_UNKNOWN';
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/conversion?`;
      const queryParams = {};
      const bodyParams = {};
      const {
        name,
        item_name,
        itemName = item_name || name,
        customer_id,
        customerId = customer_id,
        item_id,
        itemId = item_id || customerId,
        variation_id,
        variationId = variation_id,
        revenue,
        section = 'Products',
        display_name,
        displayName = display_name,
        type,
        is_custom_type,
        isCustomType = is_custom_type,
        analyticsTags,
      } = parameters;

      // Ensure support for both item_id and customer_id as parameters
      if (itemId) {
        bodyParams.item_id = itemId;
      }

      // Ensure support for both item_name and name as parameters
      if (itemName) {
        bodyParams.item_name = itemName;
      }

      if (variationId) {
        bodyParams.variation_id = variationId;
      }

      if (revenue || revenue === 0) {
        bodyParams.revenue = revenue.toString();
      }

      if (section) {
        queryParams.section = section;
        bodyParams.section = section;
      }

      if (searchTerm) {
        bodyParams.search_term = searchTerm;
      }

      if (type) {
        bodyParams.type = type;
      }

      if (isCustomType) {
        bodyParams.is_custom_type = isCustomType;
      }

      if (displayName) {
        bodyParams.display_name = displayName;
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      const requestURL = `${requestPath}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send purchase event to API
   *
   * @function trackPurchase
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {Array.<{itemId: string | undefined,
   * variationId: string | undefined,
   * itemName: string | undefined,
   * count: number | undefined,
   * price: number | undefined}>} parameters.items - List of product item objects
   * @param {number} parameters.revenue - The subtotal (excluding taxes, shipping, etc.) of the entire order
   * @param {string} [parameters.orderId] - Unique order identifier
   * @param {string} [parameters.section="Products"] - Index section
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User completed an order (usually fired on order confirmation page)
   * @example
   * constructorio.tracker.trackPurchase(
   *     {
   *         items: [{ itemId: 'KMH876', price: 1, count: 1}, { itemId: 'KMH140', price: 1, count: 1s }],
   *         revenue: 12.00,
   *         orderId: 'OUNXBG2HMA',
   *         section: 'Products',
   *     },
   * );
   */
  trackPurchase(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/purchase?`;
      const queryParams = {};
      const bodyParams = {};
      const {
        items,
        revenue,
        order_id,
        orderId = order_id,
        section,
        analyticsTags,
      } = parameters;
      const { apiKey } = this.options;

      if (orderId) {
        // Don't send another purchase event if we have already tracked the order for the current key
        if (helpers.hasOrderIdRecord({ orderId, apiKey })) {
          return false;
        }

        helpers.addOrderIdRecord({ orderId, apiKey });

        // Add order_id to the tracking params
        bodyParams.order_id = orderId;
      }

      if (items && Array.isArray(items)) {
        bodyParams.items = items.slice(0, 100).map((item) => helpers.toSnakeCaseKeys(item, false));
      }

      if (revenue || revenue === 0) {
        bodyParams.revenue = revenue;
      }

      if (section) {
        queryParams.section = section;
      } else {
        queryParams.section = 'Products';
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      const requestURL = `${requestPath}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send recommendation view event to API
   *
   * @function trackRecommendationView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.url - Current page URL
   * @param {string} parameters.podId - Pod identifier
   * @param {number} parameters.numResultsViewed - Number of results viewed
   * @param {object[]} [parameters.items] - List of Product Item Objects
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - Page number of results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {string} [parameters.resultId] - Recommendation result identifier (returned in response from Constructor)
   * @param {string} [parameters.section="Products"] - Results section
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {string[]|string|number} [parameters.seedItemIds] - Item ID(s) to be used as seed
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a set of recommendations
   * @example
   * constructorio.tracker.trackRecommendationView(
   *     {
   *         items: [{ itemId: 'KMH876' }, { itemId: 'KMH140' }],
   *         resultCount: 22,
   *         resultPage: 2,
   *         resultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         url: 'https://demo.constructor.io/sandbox/farmstand',
   *         podId: '019927c2-f955-4020',
   *         numResultsViewed: 3,
   *     },
   * );
   */
  trackRecommendationView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/recommendation_result_view?`;
      const bodyParams = {};
      const {
        result_count,
        result_page,
        resultPage = result_page,
        result_offset,
        resultOffset = result_offset,
        result_id,
        resultId = result_id,
        section,
        url,
        pod_id,
        podId = pod_id,
        num_results_viewed,
        numResultsViewed = num_results_viewed,
        items,
        analyticsTags,
        seedItemIds,
        resultCount = result_count || items?.length || 0,
      } = parameters;

      if (!helpers.isNil(resultCount)) {
        bodyParams.result_count = resultCount;
      }

      if (!helpers.isNil(resultPage)) {
        bodyParams.result_page = resultPage;
      }

      if (!helpers.isNil(resultOffset)) {
        bodyParams.result_offset = resultOffset;
      }

      if (resultId) {
        bodyParams.result_id = resultId;
      }

      if (section) {
        bodyParams.section = section;
      } else {
        bodyParams.section = 'Products';
      }

      if (url) {
        bodyParams.url = helpers.truncateString(url, MAX_URL_LENGTH);
      }

      if (podId) {
        bodyParams.pod_id = podId;
      }

      if (!helpers.isNil(numResultsViewed)) {
        bodyParams.num_results_viewed = numResultsViewed;
      }

      if (items && Array.isArray(items)) {
        bodyParams.items = items.slice(0, 100).map((item) => helpers.toSnakeCaseKeys(item, false));
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      if (typeof seedItemIds === 'number') {
        bodyParams.seed_item_ids = [String(seedItemIds)];
      } else if (seedItemIds?.length && typeof seedItemIds === 'string') {
        bodyParams.seed_item_ids = [seedItemIds];
      } else if (seedItemIds?.length && Array.isArray(seedItemIds)) {
        bodyParams.seed_item_ids = seedItemIds;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send results impression view event to API
   *
   * @function trackResultsImpressionView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {object[]} parameters.items - List of product items viewed (required)
   * @param {string} parameters.items[].itemId - Item identifier used for merchandising (required)
   * @param {string} parameters.items[].itemName - Display name for the item (required)
   * @param {string} [parameters.items[].variationId] - Variation identifier when applicable
   * @param {string} [parameters.items[].slCampaignId] - Sponsored listings campaign identifier
   * @param {string} [parameters.items[].slCampaignOwner] - Sponsored listings campaign owner
   * @param {string} [parameters.filterName] - Filter name from the relevant browse page
   * @param {string} [parameters.filterValue] - Filter value from the relevant browse page
   * @param {string} [parameters.searchTerm] - Search query of the relevant search page
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed results (items became visible in viewport)
   * @example
   * constructorio.tracker.trackResultsImpressionView(
   *     {
   *         items: [
   *             { itemId: 'KMH876', itemName: 'Red T-Shirt' },
   *             { itemId: 'KMH140', itemName: 'Blue Jeans' },
   *         ],
   *         searchTerm: 'shirt',
   *     },
   * );
   */
  trackResultsImpressionView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const { items, filterName, filterValue, searchTerm } = parameters;

      // Ensure items are provided (required)
      if (!items || !Array.isArray(items) || items.length === 0) {
        return new Error('items is a required parameter of type array');
      }

      // Validate each item has required fields and is an object
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
          return new Error('All items must be objects');
        }
        if (!item.itemId || typeof item.itemId !== 'string') {
          return new Error('itemId is a required property of type string for each item');
        }
        if (!item.itemName || typeof item.itemName !== 'string') {
          return new Error('itemName is a required property of type string for each item');
        }
      }

      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/impression_view?`;
      const transformedItems = items.map((item) => helpers.toSnakeCaseKeys(item, false));

      const bodyParams = {
        items: transformedItems,
      };

      if (filterName) {
        bodyParams.filter_name = filterName;
      }

      if (filterValue) {
        bodyParams.filter_value = filterValue;
      }

      if (searchTerm) {
        bodyParams.search_term = searchTerm;
      }

      const requestURL = `${baseUrl}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send media impression view event to API
   *
   * @function trackMediaImpressionView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.bannerAdId - Banner ad identifier
   * @param {string} parameters.placementId - Placement identifier
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a media banner
   * @example
   * constructorio.tracker.trackMediaImpressionView(
   *     {
   *         bannerAdId: 'banner_ad_id',
   *         placementId: 'placement_id',
   *     },
   * );
   */
  trackMediaImpressionView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = helpers.getBehaviorUrl(this.options.mediaServiceUrl);

      const requestPath = `${baseUrl.toString()}v2/ad_behavioral_action/display_ad_view?`;

      const bodyParams = {};
      const {
        bannerAdId,
        placementId,
        analyticsTags,
      } = parameters;

      if (!helpers.isNil(bannerAdId)) {
        bodyParams.banner_ad_id = bannerAdId;
      }

      if (!helpers.isNil(placementId)) {
        bodyParams.placement_id = placementId;
      }

      if (!helpers.isNil(analyticsTags)) {
        bodyParams.analytics_tags = analyticsTags;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send media impression click event to API
   *
   * @function trackMediaImpressionClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.bannerAdId - Banner ad identifier
   * @param {string} parameters.placementId - Placement identifier
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a media banner
   * @example
   * constructorio.tracker.trackMediaImpressionClick(
   *     {
   *         bannerAdId: 'banner_ad_id',
   *         placementId: 'placement_id',
   *     },
   * );
   */
  trackMediaImpressionClick(parameters, networkParameters = {}) {
    // Ensure required parameters are provided
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = helpers.getBehaviorUrl(this.options.mediaServiceUrl);

      const requestPath = `${baseUrl.toString()}v2/ad_behavioral_action/display_ad_click?`;

      const bodyParams = {};
      const {
        bannerAdId,
        placementId,
        analyticsTags,
      } = parameters;

      if (!helpers.isNil(bannerAdId)) {
        bodyParams.banner_ad_id = bannerAdId;
      }

      if (!helpers.isNil(placementId)) {
        bodyParams.placement_id = placementId;
      }

      if (!helpers.isNil(analyticsTags)) {
        bodyParams.analytics_tags = analyticsTags;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send recommendation click event to API
   *
   * @function trackRecommendationClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.podId - Pod identifier
   * @param {string} parameters.strategyId - Strategy identifier
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} parameters.itemName - Product item name
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.section="Products"] - Index section
   * @param {string} [parameters.resultId] - Recommendation result identifier (returned in response from Constructor)
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - Page number of results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {number} [parameters.resultPositionOnPage] - Position of result on page
   * @param {number} [parameters.numResultsPerPage] - Number of results on page
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @param {string} [parameters.slCampaignId] - Pass campaign id of sponsored listing
   * @param {string} [parameters.slCampaignOwner] - Pass campaign owner of sponsored listing
   * @param {string[]|string|number} [parameters.seedItemIds] - Item ID(s) to be used as seed
   * @returns {(true|Error)}
   * @description User clicked an item that appeared within a list of recommended results
   * @example
   * constructorio.tracker.trackRecommendationClick(
   *     {
   *         variationId: 'KMH879-7632',
   *         resultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         resultCount: 22,
   *         resultPage: 2,
   *         resultPositionOnPage: 2,
   *         numResultsPerPage: 12,
   *         podId: '019927c2-f955-4020',
   *         strategyId: 'complimentary',
   *         itemId: 'KMH876',
   *         itemName: 'Socks',
   *         seedItemIds: ['item-123', 'item-456'],
   *     },
   * );
   */
  trackRecommendationClick(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/recommendation_result_click?`;
      const bodyParams = {};
      const {
        variation_id,
        variationId = variation_id,
        section = 'Products',
        result_id,
        resultId = result_id,
        result_count,
        resultCount = result_count,
        result_page,
        resultPage = result_page,
        result_offset,
        resultOffset = result_offset,
        result_position_on_page,
        resultPositionOnPage = result_position_on_page,
        num_results_per_page,
        numResultsPerPage = num_results_per_page,
        pod_id,
        podId = pod_id,
        strategy_id,
        strategyId = strategy_id,
        item_id,
        itemId = item_id,
        item_name,
        itemName = item_name,
        analyticsTags,
        slCampaignId,
        slCampaignOwner,
        seedItemIds,
      } = parameters;

      if (variationId) {
        bodyParams.variation_id = variationId;
      }

      if (section) {
        bodyParams.section = section;
      }

      if (resultId) {
        bodyParams.result_id = resultId;
      }

      if (!helpers.isNil(resultCount)) {
        bodyParams.result_count = resultCount;
      }

      if (!helpers.isNil(resultPage)) {
        bodyParams.result_page = resultPage;
      }

      if (!helpers.isNil(resultOffset)) {
        bodyParams.result_offset = resultOffset;
      }

      if (!helpers.isNil(resultPositionOnPage)) {
        bodyParams.result_position_on_page = resultPositionOnPage;
      }

      if (!helpers.isNil(numResultsPerPage)) {
        bodyParams.num_results_per_page = numResultsPerPage;
      }

      if (podId) {
        bodyParams.pod_id = podId;
      }

      if (strategyId) {
        bodyParams.strategy_id = strategyId;
      }

      if (itemId) {
        bodyParams.item_id = itemId;
      }

      if (itemName) {
        bodyParams.item_name = itemName;
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      if (slCampaignId) {
        bodyParams.sl_campaign_id = slCampaignId;
      }

      if (slCampaignOwner) {
        bodyParams.sl_campaign_owner = slCampaignOwner;
      }

      if (typeof seedItemIds === 'number') {
        bodyParams.seed_item_ids = [String(seedItemIds)];
      } else if (seedItemIds?.length && typeof seedItemIds === 'string') {
        bodyParams.seed_item_ids = [seedItemIds];
      } else if (seedItemIds?.length && Array.isArray(seedItemIds)) {
        bodyParams.seed_item_ids = seedItemIds;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send browse results loaded event to API
   *
   * @function trackBrowseResultsLoaded
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.url - Current page URL
   * @param {string} parameters.filterName - Filter name
   * @param {string} parameters.filterValue - Filter value
   * @param {object[]} parameters.items - List of product item objects
   * @param {string} [parameters.section="Products"] - Index section
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - Page number of results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {string} [parameters.resultId] - Browse result identifier (returned in response from Constructor)
   * @param {object} [parameters.selectedFilters] - Selected filters
   * @param {string} [parameters.sortOrder] - Sort order ('ascending' or 'descending')
   * @param {string} [parameters.sortBy] - Sorting method
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a browse product listing page
   * @example
   * constructorio.tracker.trackBrowseResultsLoaded(
   *     {
   *         resultCount: 22,
   *         resultPage: 2,
   *         resultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         selectedFilters: { brand: ['foo'], color: ['black'] },
   *         sortOrder: 'ascending',
   *         sortBy: 'price',
   *         items: [{ itemId: 'KMH876' }, { itemId: 'KMH140' }],
   *         url: 'https://demo.constructor.io/sandbox/farmstand',
   *         filterName: 'brand',
   *         filterValue: 'XYZ',
   *     },
   * );
   */
  trackBrowseResultsLoaded(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/browse_result_load?`;
      const bodyParams = {};
      const {
        section = 'Products',
        result_count,
        result_page,
        resultPage = result_page,
        result_offset,
        resultOffset = result_offset,
        result_id,
        resultId = result_id,
        selected_filters,
        selectedFilters = selected_filters,
        url,
        sort_order,
        sortOrder = sort_order,
        sort_by,
        sortBy = sort_by,
        filter_name,
        filterName = filter_name,
        filter_value,
        filterValue = filter_value,
        items,
        analyticsTags,
        resultCount = result_count ?? items?.length ?? 0,
      } = parameters;

      if (section) {
        bodyParams.section = section;
      }

      if (!helpers.isNil(resultCount)) {
        bodyParams.result_count = resultCount;
      }

      if (!helpers.isNil(resultPage)) {
        bodyParams.result_page = resultPage;
      }

      if (!helpers.isNil(resultOffset)) {
        bodyParams.result_offset = resultOffset;
      }

      if (resultId) {
        bodyParams.result_id = resultId;
      }

      if (selectedFilters) {
        bodyParams.selected_filters = selectedFilters;
      }

      if (url) {
        bodyParams.url = helpers.truncateString(url, MAX_URL_LENGTH);
      }

      if (sortOrder) {
        bodyParams.sort_order = sortOrder;
      }

      if (sortBy) {
        bodyParams.sort_by = sortBy;
      }

      if (filterName) {
        bodyParams.filter_name = filterName;
      }

      if (filterValue) {
        bodyParams.filter_value = filterValue;
      }

      if (items && Array.isArray(items)) {
        bodyParams.items = items.map((item) => helpers.toSnakeCaseKeys(item, false));
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send browse result click event to API
   *
   * @function trackBrowseResultClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.filterName - Filter name
   * @param {string} parameters.filterValue - Filter value
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} parameters.itemName - Product item name
   * @param {string} [parameters.section="Products"] - Index section
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.resultId] - Browse result identifier (returned in response from Constructor)
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - Page number of results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {number} [parameters.resultPositionOnPage] - Position of clicked item
   * @param {number} [parameters.numResultsPerPage] - Number of results shown
   * @param {object} [parameters.selectedFilters] -  Selected filters
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {string} [parameters.slCampaignId] - Pass campaign id of sponsored listing
   * @param {string} [parameters.slCampaignOwner] - Pass campaign owner of sponsored listing
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a result that appeared within a browse product listing page
   * @example
   * constructorio.tracker.trackBrowseResultClick(
   *     {
   *         variationId: 'KMH879-7632',
   *         resultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         resultCount: 22,
   *         resultPage: 2,
   *         resultPositionOnPage: 2,
   *         numResultsPerPage: 12,
   *         selectedFilters: { brand: ['foo'], color: ['black'] },
   *         filterName: 'brand',
   *         filterValue: 'XYZ',
   *         itemId: 'KMH876',
   *         slCampaignId: 'Campaign 123',
   *         slCampaignOwner: 'Store 123',
   *     },
   * );
   */
  trackBrowseResultClick(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/browse_result_click?`;
      const bodyParams = {};
      const {
        section = 'Products',
        variation_id,
        variationId = variation_id,
        result_id,
        resultId = result_id,
        result_count,
        resultCount = result_count,
        result_page,
        resultPage = result_page,
        result_offset,
        resultOffset = result_offset,
        result_position_on_page,
        resultPositionOnPage = result_position_on_page,
        num_results_per_page,
        numResultsPerPage = num_results_per_page,
        selected_filters,
        selectedFilters = selected_filters,
        filter_name,
        filterName = filter_name,
        filter_value,
        filterValue = filter_value,
        customer_id,
        customerId = customer_id,
        item_id,
        itemId = customerId || item_id,
        item_name,
        name,
        itemName = item_name || name,
        analyticsTags,
        slCampaignId,
        slCampaignOwner,
      } = parameters;

      if (section) {
        bodyParams.section = section;
      }

      if (variationId) {
        bodyParams.variation_id = variationId;
      }

      if (resultId) {
        bodyParams.result_id = resultId;
      }

      if (!helpers.isNil(resultCount)) {
        bodyParams.result_count = resultCount;
      }

      if (!helpers.isNil(resultPage)) {
        bodyParams.result_page = resultPage;
      }

      if (!helpers.isNil(resultOffset)) {
        bodyParams.result_offset = resultOffset;
      }

      if (!helpers.isNil(resultPositionOnPage)) {
        bodyParams.result_position_on_page = resultPositionOnPage;
      }

      if (!helpers.isNil(numResultsPerPage)) {
        bodyParams.num_results_per_page = numResultsPerPage;
      }

      if (selectedFilters) {
        bodyParams.selected_filters = selectedFilters;
      }

      if (filterName) {
        bodyParams.filter_name = filterName;
      }

      if (filterValue) {
        bodyParams.filter_value = filterValue;
      }

      if (itemId) {
        bodyParams.item_id = itemId;
      }

      // Ensure support for both item_name and name as parameters
      if (itemName) {
        bodyParams.item_name = itemName;
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      if (slCampaignId) {
        bodyParams.sl_campaign_id = slCampaignId;
      }

      if (slCampaignOwner) {
        bodyParams.sl_campaign_owner = slCampaignOwner;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send browse redirect event to API
   * @function trackBrowseRedirect
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.searchTerm - The search query that caused redirect
   * @param {string} parameters.filterName - Filter name of the browse page the user is redirected to. Should be the same value sent in `trackBrowseResultsLoaded`
   * @param {string} parameters.filterValue - Filter value of the browse page the user is redirected to. Should be the same value sent in `trackBrowseResultsLoaded`
   * @param {string} [parameters.userInput] - The text that a user had typed at the moment when submitting search request
   * @param {string} [parameters.redirectToUrl] - URL of the  page to which user is redirected
   * @param {string} [parameters.section="Products"] - Index section
   * @param {object} [parameters.selectedFilters] - Selected filters
   * @param {string} [parameters.sortOrder] - Sort order ('ascending' or 'descending')
   * @param {string} [parameters.sortBy] - Sorting method
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User got redirected to a browse product listing page
   * @example
   * constructorio.tracker.trackBrowseRedirect(
   *     {
   *         searchTerm: "books",
   *         filterName: 'brand',
   *         filterValue: 'XYZ',
   *         redirectToUrl: 'https://demo.constructor.io/books',
   *         selectedFilters: { brand: ['foo'], color: ['black'] },
   *         sortOrder: 'ascending',
   *         sortBy: 'price',
   *     },
   * );
   */
  trackBrowseRedirect(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/browse_redirect?`;
      const bodyParams = {};
      const {
        searchTerm,
        userInput,
        section = 'Products',
        selectedFilters,
        redirectToUrl,
        sortOrder,
        sortBy,
        filterName,
        filterValue,
        analyticsTags,
      } = parameters;

      if (searchTerm) {
        bodyParams.search_term = searchTerm;
      }

      if (userInput) {
        bodyParams.user_input = userInput;
      }

      if (redirectToUrl) {
        bodyParams.redirect_to_url = redirectToUrl;
      }

      if (section) {
        bodyParams.section = section;
      }

      if (selectedFilters) {
        bodyParams.selected_filters = selectedFilters;
      }

      if (sortOrder) {
        bodyParams.sort_order = sortOrder;
      }

      if (sortBy) {
        bodyParams.sort_by = sortBy;
      }

      if (filterName) {
        bodyParams.filter_name = filterName;
      }

      if (filterValue) {
        bodyParams.filter_value = filterValue;
      }

      if (analyticsTags) {
        bodyParams.analytics_tags = analyticsTags;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send generic result click event to API
   *
   * @function trackGenericResultClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} [parameters.itemName] - Product item name
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.section="Products"] - Index section
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites
   * @param {object} [parameters.analyticsTags] - Pass additional analytics data
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a result that appeared outside of the scope of search / browse / recommendations
   * @example
   * constructorio.tracker.trackGenericResultClick(
   *     {
   *         itemId: 'KMH876',
   *         itemName: 'Red T-Shirt',
   *         variationId: 'KMH879-7632',
   *     },
   * );
   */
  trackGenericResultClick(parameters, networkParameters = {}) {
    // Ensure required parameters are provided
    if (typeof parameters === 'object') {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/result_click?`;
      const bodyParams = {};
      const {
        item_id,
        itemId = item_id,
        item_name,
        itemName = item_name,
        variation_id,
        variationId = variation_id,
        section = 'Products',
        result_offset,
        resultOffset = result_offset,
        analyticsTags,
      } = parameters;
      if (itemId) {
        bodyParams.section = section;
        bodyParams.item_id = itemId;

        if (itemName) {
          bodyParams.item_name = itemName;
        }

        if (variationId) {
          bodyParams.variation_id = variationId;
        }

        if (!helpers.isNil(resultOffset)) {
          bodyParams.result_offset = resultOffset;
        }

        if (analyticsTags) {
          bodyParams.analytics_tags = analyticsTags;
        }

        const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
        const requestMethod = 'POST';
        const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

        this.requests.queue(
          requestURL,
          requestMethod,
          requestBody,
          networkParameters,
        );
        this.requests.send();

        return true;
      }
    }

    this.requests.send();

    return new Error('A parameters object with an "itemId" property is required.');
  }

  /**
   * Send quiz results loaded event to API
   *
   * @function trackQuizResultsLoaded
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.quizId - Quiz identifier
   * @param {string} parameters.quizVersionId - Quiz version identifier
   * @param {string} parameters.quizSessionId - Quiz session identifier associated with this conversion event
   * @param {string} parameters.url - Current page url
   * @param {object[]} parameters.items - List of product item objects
   * @param {string} [parameters.section='Products'] - Index section
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - The page of the results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {string} [parameters.resultId] - Quiz result identifier (returned in response from Constructor)
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a quiz results page
   * @example
   * constructorio.tracker.trackQuizResultsLoaded(
   *     {
   *         quizId: 'coffee-quiz',
   *         quizVersionId: '1231244',
   *         quizSessionId: '3123',
   *         url: 'www.example.com',
   *         resultCount: 167,
   *         items: [{ itemId: 'KMH876', itemName: 'coffee' }, { itemId: 'KMH140', variationId: '123' }],
   *     },
   * );
   */
  // eslint-disable-next-line complexity
  trackQuizResultsLoaded(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/quiz_result_load?`;
      const {
        quiz_id,
        quizId = quiz_id,
        quiz_version_id,
        quizVersionId = quiz_version_id,
        quiz_session_id,
        quizSessionId = quiz_session_id,
        url,
        section = 'Products',
        result_count,
        resultCount = result_count,
        result_id,
        resultId = result_id,
        result_page,
        resultPage = result_page,
        result_offset,
        resultOffset = result_offset,
        items,
      } = parameters;
      const queryParams = {};
      const bodyParams = {};

      if (items && Array.isArray(items)) {
        bodyParams.items = items.slice(0, 100).map((item) => helpers.toSnakeCaseKeys(item, false));
      }

      if (typeof quizId !== 'string') {
        return new Error('"quizId" is a required parameter of type string');
      }

      if (typeof quizVersionId !== 'string') {
        return new Error('"quizVersionId" is a required parameter of type string');
      }

      if (typeof quizSessionId !== 'string') {
        return new Error('"quizSessionId" is a required parameter of type string');
      }

      if (typeof url !== 'string') {
        return new Error('"url" is a required parameter of type string');
      }

      bodyParams.quiz_id = quizId;
      bodyParams.quiz_version_id = quizVersionId;
      bodyParams.quiz_session_id = quizSessionId;
      bodyParams.url = helpers.truncateString(url, MAX_URL_LENGTH);

      if (!helpers.isNil(section)) {
        if (typeof section !== 'string') {
          return new Error('"section" must be a string');
        }
        queryParams.section = section;
        bodyParams.section = section;
      }

      if (!helpers.isNil(resultCount)) {
        if (typeof resultCount !== 'number') {
          return new Error('"resultCount" must be a number');
        }
        bodyParams.result_count = resultCount;
      }

      if (!helpers.isNil(resultId)) {
        if (typeof resultId !== 'string') {
          return new Error('"resultId" must be a string');
        }
        bodyParams.result_id = resultId;
      }

      if (!helpers.isNil(resultPage)) {
        if (typeof resultPage !== 'number') {
          return new Error('"resultPage" must be a number');
        }
        bodyParams.result_page = resultPage;
      }

      if (!helpers.isNil(resultOffset)) {
        if (typeof resultOffset !== 'number') {
          return new Error('"resultOffset" must be a number');
        }
        bodyParams.result_offset = resultOffset;
      }

      const requestURL = `${requestPath}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send quiz result click event to API
   *
   * @function trackQuizResultClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.quizId - Quiz identifier
   * @param {string} parameters.quizVersionId - Quiz version identifier
   * @param {string} parameters.quizSessionId - Quiz session identifier associated with this conversion event
   * @param {string} [parameters.itemId] - Product item unique identifier (Either itemId or itemName is required)
   * @param {string} [parameters.itemName] - Product item name
   * @param {string} [parameters.variationId] - Product variation unique identifier
   * @param {string} [parameters.section='Products'] - Index section
   * @param {number} [parameters.resultCount] - Total number of results
   * @param {number} [parameters.resultPage] - The page of the results
   * @param {number} [parameters.resultOffset] - Current offset of results, used on scrolling sites. Cannot be used with `resultPage`
   * @param {string} [parameters.resultId] - Quiz result identifier (returned in response from Constructor)
   * @param {number} [parameters.resultPositionOnPage] - Position of clicked item
   * @param {number} [parameters.numResultsPerPage] - Number of results shown
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a quiz results page
   * @example
   * constructorio.tracker.trackQuizResultClick(
   *     {
   *         quizId: 'coffee-quiz',
   *         quizVersionId: '1231244',
   *         quizSessionId: '123',
   *         itemId: '123',
   *         itemName: 'espresso'
   *         variationId: '733431',
   *     },
   * );
   */
  // eslint-disable-next-line complexity
  trackQuizResultClick(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/quiz_result_click?`;
      const {
        quiz_id,
        quizId = quiz_id,
        quiz_version_id,
        quizVersionId = quiz_version_id,
        quiz_session_id,
        quizSessionId = quiz_session_id,
        item_id,
        itemId = item_id,
        item_name,
        itemName = item_name,
        result_count,
        resultCount = result_count,
        result_id,
        resultId = result_id,
        result_page,
        resultPage = result_page,
        result_offset,
        resultOffset = result_offset,
        num_results_per_page,
        numResultsPerPage = num_results_per_page,
        result_position_on_page,
        resultPositionOnPage = result_position_on_page,
        section = 'Products',
        variationId,
      } = parameters;

      const queryParams = {};
      const bodyParams = {};

      if (typeof quizId !== 'string') {
        return new Error('"quizId" is a required parameter of type string');
      }

      if (typeof quizVersionId !== 'string') {
        return new Error('"quizVersionId" is a required parameter of type string');
      }

      if (typeof quizSessionId !== 'string') {
        return new Error('"quizSessionId" is a required parameter of type string');
      }

      if (typeof itemId !== 'string' && typeof itemName !== 'string') {
        return new Error('"itemId" or "itemName" is a required parameter of type string');
      }

      bodyParams.quiz_id = quizId;
      bodyParams.quiz_version_id = quizVersionId;
      bodyParams.quiz_session_id = quizSessionId;

      if (!helpers.isNil(itemId)) {
        if (typeof itemId !== 'string') {
          return new Error('"itemId" must be a string');
        }
        bodyParams.item_id = itemId;
      }

      if (!helpers.isNil(itemName)) {
        if (typeof itemName !== 'string') {
          return new Error('"itemName" must be a string');
        }
        bodyParams.item_name = itemName;
      }

      if (!helpers.isNil(variationId)) {
        if (typeof variationId !== 'string') {
          return new Error('"variationId" must be a string');
        }
        bodyParams.variation_id = variationId;
      }

      if (!helpers.isNil(section)) {
        if (typeof section !== 'string') {
          return new Error('"section" must be a string');
        }
        queryParams.section = section;
      }

      if (!helpers.isNil(resultCount)) {
        if (typeof resultCount !== 'number') {
          return new Error('"resultCount" must be a number');
        }
        bodyParams.result_count = resultCount;
      }

      if (!helpers.isNil(resultId)) {
        if (typeof resultId !== 'string') {
          return new Error('"resultId" must be a string');
        }
        bodyParams.result_id = resultId;
      }

      if (!helpers.isNil(resultPage)) {
        if (typeof resultPage !== 'number') {
          return new Error('"resultPage" must be a number');
        }
        bodyParams.result_page = resultPage;
      }

      if (!helpers.isNil(resultOffset)) {
        if (typeof resultOffset !== 'number') {
          return new Error('"resultOffset" must be a number');
        }
        bodyParams.result_offset = resultOffset;
      }

      if (!helpers.isNil(numResultsPerPage)) {
        if (typeof numResultsPerPage !== 'number') {
          return new Error('"numResultsPerPage" must be a number');
        }
        bodyParams.num_results_per_page = numResultsPerPage;
      }

      if (!helpers.isNil(resultPositionOnPage)) {
        if (typeof resultPositionOnPage !== 'number') {
          return new Error('"resultPositionOnPage" must be a number');
        }
        bodyParams.result_position_on_page = resultPositionOnPage;
      }

      const requestURL = `${requestPath}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send quiz conversion event to API
   *
   * @function trackQuizConversion
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.quizId - Quiz identifier
   * @param {string} parameters.quizVersionId - Quiz version identifier
   * @param {string} parameters.quizSessionId - Quiz session identifier associated with this conversion event
   * @param {string} [parameters.itemId] - Product item unique identifier (Either itemId or itemName is required)
   * @param {string} [parameters.itemName] - Product item name
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.revenue] - Sale price if available, otherwise the regular (retail) price of item
   * @param {string} [parameters.section='Products'] - Index section
   * @param {string} [parameters.type='add_to_cart'] - Conversion type
   * @param {boolean} [parameters.isCustomType] - Specify if type is custom conversion type
   * @param {string} [parameters.displayName] - Display name for the custom conversion type
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a quiz results page
   * @example
   * constructorio.tracker.trackQuizConversion(
   *     {
   *         quizId: 'coffee-quiz',
   *         quizVersionId: '1231244',
   *         quizSessionId: '3123',
   *         itemName: 'espresso',
   *         variationId: '167',
   *         type: 'add_to_cart',
   *         revenue: '1.0'
   *     },
   * );
   */
  // eslint-disable-next-line complexity
  trackQuizConversion(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/quiz_conversion?`;
      const {
        quiz_id,
        quizId = quiz_id,
        quiz_version_id,
        quizVersionId = quiz_version_id,
        quiz_session_id,
        quizSessionId = quiz_session_id,
        item_id,
        itemId = item_id,
        item_name,
        itemName = item_name,
        variation_id,
        variationId = variation_id,
        revenue,
        section = 'Products',
        type,
        is_custom_type,
        isCustomType = is_custom_type,
        display_name,
        displayName = display_name,
      } = parameters;

      const queryParams = {};
      const bodyParams = {};

      // Ensure required parameters provided
      if (typeof quizId !== 'string') {
        return new Error('"quizId" is a required parameter of type string');
      }

      if (typeof quizVersionId !== 'string') {
        return new Error('"quizVersionId" is a required parameter of type string');
      }

      if (typeof quizSessionId !== 'string') {
        return new Error('"quizSessionId" is a required parameter of type string');
      }

      if (typeof itemId !== 'string' && typeof itemName !== 'string') {
        return new Error('"itemId" or "itemName" is a required parameter of type string');
      }

      bodyParams.quiz_id = quizId;
      bodyParams.quiz_version_id = quizVersionId;
      bodyParams.quiz_session_id = quizSessionId;

      if (!helpers.isNil(itemId)) {
        if (typeof itemId !== 'string') {
          return new Error('"itemId" must be a string');
        }
        bodyParams.item_id = itemId;
      }

      if (!helpers.isNil(itemName)) {
        if (typeof itemName !== 'string') {
          return new Error('"itemName" must be a string');
        }
        bodyParams.item_name = itemName;
      }

      if (!helpers.isNil(variationId)) {
        if (typeof variationId !== 'string') {
          return new Error('"variationId" must be a string');
        }
        bodyParams.variation_id = variationId;
      }

      if (!helpers.isNil(revenue)) {
        if (typeof revenue !== 'string') {
          return new Error('"revenue" must be a string');
        }
        bodyParams.revenue = revenue;
      }

      if (!helpers.isNil(section)) {
        if (typeof section !== 'string') {
          return new Error('"section" must be a string');
        }
        bodyParams.section = section;
      }

      if (!helpers.isNil(type)) {
        if (typeof type !== 'string') {
          return new Error('"type" must be a string');
        }
        bodyParams.type = type;
      }

      if (!helpers.isNil(isCustomType)) {
        if (typeof isCustomType !== 'boolean') {
          return new Error('"isCustomType" must be a boolean');
        }
        bodyParams.is_custom_type = isCustomType;
      }

      if (!helpers.isNil(displayName)) {
        if (typeof displayName !== 'string') {
          return new Error('"displayName" must be a string');
        }
        bodyParams.display_name = displayName;
      }

      const requestURL = `${requestPath}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send ASA request submitted event
   *
   * @function trackAgentSubmit
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.source] - How the intent was submitted Ex. "suggestion"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted an agent search
   *   (pressing enter within agent input element, or clicking an agent submit or
   *   suggested question element)
   * @example
   * constructorio.tracker.trackAgentSubmit(
   *     {
   *         intent: 'show me a recipe for a cookie',
   *         source: 'suggestion',
   *     },
   * );
   */
  trackAgentSubmit(parameters, networkParameters = {}) {
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      // Ensure parameters are provided (required)
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/assistant_submit?`;
      const {
        section,
        intent,
        threadId,
        source,
      } = parameters;
      const bodyParams = {
        intent,
        section,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (source) {
        bodyParams.source = source;
      }

      const requestURL = `${baseUrl}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send agent results page load started
   *
   * @function trackAgentResultLoadStarted
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - The intent result id from the ASA response
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description Agent results page load begun (but has not necessarily loaded completely)
   * @example
   * constructorio.tracker.trackAgentResultLoadStarted(
   *     {
   *         intent: 'show me a recipe for a cookie',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *     },
   * );
   */
  trackAgentResultLoadStarted(parameters, networkParameters = {}) {
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      // Ensure parameters are provided (required)
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/assistant_result_load_start?`;
      const {
        section,
        intentResultId,
        intent,
        threadId,
      } = parameters;
      const bodyParams = {
        intent_result_id: intentResultId,
        intent,
        section,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      const requestURL = `${baseUrl}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send agent results page load finished
   *
   * @function trackAgentResultLoadFinished
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {number} parameters.searchResultCount - Number of search results loaded
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - The intent result id from the ASA response
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description Agent results page load finished
   * @example
   * constructorio.tracker.trackAgentResultLoadFinished(
   *     {
   *         intent: 'show me a recipe for a cookie',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         searchResultCount: 5,
   *     },
   * );
   */
  trackAgentResultLoadFinished(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/assistant_result_load_finish?`;
      const {
        section,
        searchResultCount,
        intentResultId,
        intent,
        threadId,
      } = parameters;
      const bodyParams = {
        intent_result_id: intentResultId,
        section,
        intent,
        search_result_count: searchResultCount,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      const requestURL = `${baseUrl}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send agent result click event to API
   *
   * @function trackAgentResultClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - intent of the user
   * @param {string} parameters.searchResultId - result_id of the specific search result the clicked item belongs to
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} parameters.itemName - Product item name
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.intentResultId] - Browse result identifier (returned in response from Constructor)
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a result that appeared within an agent search result
   * @example
   * constructorio.tracker.trackAgentResultClick(
   *     {
   *         variationId: 'KMH879-7632',
   *         searchResultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         intent: 'show me a recipe for a cookie',
   *         itemId: 'KMH876',
   *     },
   * );
   */
  trackAgentResultClick(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/assistant_search_result_click?`;
      const {
        section = 'Products',
        variationId,
        intentResultId,
        searchResultId,
        itemId,
        itemName,
        intent,
        threadId,
      } = parameters;

      const bodyParams = {
        section,
        variation_id: variationId,
        intent_result_id: intentResultId,
        search_result_id: searchResultId,
        item_id: itemId,
        item_name: itemName,
        intent,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send agent search result view event to API
   *
   * @function trackAgentResultView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - intent of the user
   * @param {string} parameters.searchResultId - result_id of the specific search result the clicked item belongs to
   * @param {number} parameters.numResultsViewed - Number of items viewed in this search result
   * @param {object[]} [parameters.items] - List of product item objects viewed
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - Browse result identifier (returned in response from Constructor)
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a search result within an agent result
   * @example
   * constructorio.tracker.trackAgentResultView(
   *     {
   *         searchResultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         intent: 'show me a recipe for a cookie',
   *         numResultsViewed: 5,
   *         items: [{itemId: 'KMH876'}, {itemId: 'KMH140'}, {itemId: 'KMH437'}],
   *     },
   * );
   */
  trackAgentResultView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const requestPath = `${this.options.serviceUrl}/v2/behavioral_action/assistant_search_result_view?`;
      const {
        section = 'Products',
        items,
        numResultsViewed,
        intentResultId,
        searchResultId,
        intent,
        threadId,
      } = parameters;

      const bodyParams = {
        section,
        intent_result_id: intentResultId,
        search_result_id: searchResultId,
        num_results_viewed: numResultsViewed,
        items: items && Array.isArray(items) && items.slice(0, 100).map((item) => helpers.toSnakeCaseKeys(item, false)),
        intent,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      const requestURL = `${requestPath}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, { ...this.options, requestMethod });

      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();

      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send ASA search submitted event
   *
   * @function trackAgentSearchSubmit
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {string} parameters.searchTerm - Term of submitted agent search event
   * @param {string} parameters.searchResultId - resultId of search result the clicked item belongs to
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - intentResultId from the ASA response
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted an alternative agent search result search term
   * @example
   * constructorio.tracker.trackAgentSearchSubmit({
   *     {
   *         searchResultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         intent: 'show me a recipe for a cookie',
   *         searchTerm: 'flour',
   *     },
   * );
   */
  trackAgentSearchSubmit(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      // Ensure parameters are provided (required)
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/assistant_search_submit?`;
      const {
        section,
        intent,
        searchTerm,
        searchResultId,
        intentResultId,
        threadId,
      } = parameters;

      const bodyParams = {
        intent,
        section,
        search_term: searchTerm,
        search_result_id: searchResultId,
        intent_result_id: intentResultId,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      const requestURL = `${baseUrl}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send agent CTA button click event
   *
   * @function trackAgentButtonClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.mode - Agent mode the CTA opens or starts. One of "chat", "search", "qna", "recommendations", "query_refinement"
   * @param {string} parameters.agentDomain - Agent experience the CTA opens or starts Ex. "explorer"
   * @param {string} [parameters.positionOnPage] - Stable label describing where the CTA is placed Ex. "search_bar"
   * @param {string} [parameters.pageType] - Page surface where the CTA was clicked. One of "home", "plp", "pdp", "search", "collection", "email_campaign", "cart"
   * @param {number} [parameters.instanceId] - 1-based positive integer distinguishing CTA instances with the same mode and position during one page view
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a CTA whose primary action is to open or start an agent experience
   * @example
   * constructorio.tracker.trackAgentButtonClick(
   *     {
   *         mode: 'chat',
   *         agentDomain: 'explorer',
   *         positionOnPage: 'search_bar',
   *         pageType: 'pdp',
   *         instanceId: 1,
   *     },
   * );
   */
  trackAgentButtonClick(parameters, networkParameters = {}) {
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const {
        mode,
        agentDomain,
        positionOnPage,
        pageType,
        instanceId,
        section,
      } = parameters;

      if (!mode || typeof mode !== 'string') {
        this.requests.send();

        return new Error('mode is a required parameter of type string');
      }

      if (!agentDomain || typeof agentDomain !== 'string') {
        this.requests.send();

        return new Error('agentDomain is a required parameter of type string');
      }

      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/ai_agent_button_click?`;
      const bodyParams = {
        mode,
        agent_domain: agentDomain,
        section,
      };

      if (positionOnPage) {
        bodyParams.position_on_page = positionOnPage;
      }

      if (pageType) {
        bodyParams.page_type = pageType;
      }

      if (!helpers.isNil(instanceId)) {
        if (!Number.isInteger(instanceId) || instanceId < 1) {
          this.requests.send();

          return new Error('instanceId must be a positive integer');
        }

        bodyParams.instance_id = instanceId;
      }

      const requestURL = `${baseUrl}${applyParamsAsString({}, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters are required of type object');
  }

  /**
   * Send ASA request submitted event
   *
   * @deprecated This method will be removed in a future version. Use trackAgentSubmit instead.
   * @function trackAssistantSubmit
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.source] - How the intent was submitted Ex. "suggestion"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted an assistant search
   *   (pressing enter within assistant input element, or clicking an assistant submit or
   *   suggested question element)
   * @example
   * constructorio.tracker.trackAssistantSubmit(
   *     {
   *         intent: 'show me a recipe for a cookie',
   *         source: 'suggestion',
   *     },
   * );
   */
  trackAssistantSubmit(parameters, networkParameters = {}) {
    return this.trackAgentSubmit(parameters, networkParameters);
  }

  /**
   * Send assistant results page load started
   *
   * @deprecated This method will be removed in a future version. Use trackAgentResultLoadStarted instead.
   * @function trackAssistantResultLoadStarted
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - The intent result id from the ASA response
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description Assistant results page load begun (but has not necessarily loaded completely)
   * @example
   * constructorio.tracker.trackAssistantResultLoadStarted(
   *     {
   *         intent: 'show me a recipe for a cookie',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *     },
   * );
   */
  trackAssistantResultLoadStarted(parameters, networkParameters = {}) {
    return this.trackAgentResultLoadStarted(parameters, networkParameters);
  }

  /**
   * Send assistant results page load finished
   *
   * @deprecated This method will be removed in a future version. Use trackAgentResultLoadFinished instead.
   * @function trackAssistantResultLoadFinished
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {number} parameters.searchResultCount - Number of search results loaded
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - The intent result id from the ASA response
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description Assistant results page load finished
   * @example
   * constructorio.tracker.trackAssistantResultLoadFinished(
   *     {
   *         intent: 'show me a recipe for a cookie',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         searchResultCount: 5,
   *     },
   * );
   */
  trackAssistantResultLoadFinished(parameters, networkParameters = {}) {
    return this.trackAgentResultLoadFinished(parameters, networkParameters);
  }

  /**
   * Send assistant result click event to API
   *
   * @deprecated This method will be removed in a future version. Use trackAgentResultClick instead.
   * @function trackAssistantResultClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - intent of the user
   * @param {string} parameters.searchResultId - result_id of the specific search result the clicked item belongs to
   * @param {string} parameters.itemId - Product item unique identifier
   * @param {string} parameters.itemName - Product item name
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.variationId] - Product item variation unique identifier
   * @param {string} [parameters.intentResultId] - Browse result identifier (returned in response from Constructor)
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a result that appeared within an assistant search result
   * @example
   * constructorio.tracker.trackAssistantResultClick(
   *     {
   *         variationId: 'KMH879-7632',
   *         searchResultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         intent: 'show me a recipe for a cookie',
   *         itemId: 'KMH876',
   *     },
   * );
   */
  trackAssistantResultClick(parameters, networkParameters = {}) {
    return this.trackAgentResultClick(parameters, networkParameters);
  }

  /**
   * Send assistant search result view event to API
   *
   * @deprecated This method will be removed in a future version. Use trackAgentResultView instead.
   * @function trackAssistantResultView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - intent of the user
   * @param {string} parameters.searchResultId - result_id of the specific search result the clicked item belongs to
   * @param {number} parameters.numResultsViewed - Number of items viewed in this search result
   * @param {object[]} [parameters.items] - List of product item objects viewed
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - Browse result identifier (returned in response from Constructor)
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed a search result within an assistant result
   * @example
   * constructorio.tracker.trackAssistantResultView(
   *     {
   *         searchResultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         intent: 'show me a recipe for a cookie',
   *         numResultsViewed: 5,
   *         items: [{itemId: 'KMH876'}, {itemId: 'KMH140'}, {itemId: 'KMH437'}],
   *     },
   * );
   */
  trackAssistantResultView(parameters, networkParameters = {}) {
    return this.trackAgentResultView(parameters, networkParameters);
  }

  /**
   * Send ASA search submitted event
   *
   * @deprecated This method will be removed in a future version. Use trackAgentSearchSubmit instead.
   * @function trackAssistantSearchSubmit
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.intent - Intent of user request
   * @param {string} parameters.searchTerm - Term of submitted assistant search event
   * @param {string} parameters.searchResultId - resultId of search result the clicked item belongs to
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {string} [parameters.intentResultId] - intentResultId from the ASA response
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted an alternative assistant search result search term
   * @example
   * constructorio.tracker.trackAssistantSearchSubmit({
   *     {
   *         searchResultId: '019927c2-f955-4020-8b8d-6b21b93cb5a2',
   *         intentResultId: 'Zde93fd-f955-4020-8b8d-6b21b93cb5a2',
   *         intent: 'show me a recipe for a cookie',
   *         searchTerm: 'flour',
   *     },
   * );
   */
  trackAssistantSearchSubmit(parameters, networkParameters = {}) {
    return this.trackAgentSearchSubmit(parameters, networkParameters);
  }

  /**
   * Send product insights agent view events
   *
   * @function trackProductInsightsAgentViews
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {array.<{question: string}>} parameters.questions - List of pre-defined questions shown to the user
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {array.<{start: string | undefined,
   * end: string | undefined}>} parameters.viewTimespans - List of timestamp pairs in ISO_8601 format
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description The product insights agent element appeared in the visible part of the page
   * @example
   * constructorio.tracker.trackProductInsightsAgentViews({
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *     'questions': [
   *        { question: 'Why choose this?' },
   *        { question: 'How is this product made?' },
   *        { question: 'What are the dimensions of this product?' }
   *     ],
   *     'viewTimespans': [
   *       {
   *         'start': '2025-05-19T14:30:00+02:00',
   *         'end': '2025-05-19T14:30:05+02:00'
   *       },
   *       {
   *         'start': '2025-05-19T14:30:10+02:00',
   *         'end': '2025-05-19T14:30:15+02:00'
   *       }
   *     ]
   *   },
   * );
   */
  trackProductInsightsAgentViews(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_views?`;
      const {
        section,
        questions,
        itemId,
        itemName,
        variationId,
        viewTimespans,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        questions,
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
        view_timespans: viewTimespans,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent view event
   *
   * @function trackProductInsightsAgentView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {array.<{question: string}>} parameters.questions - List of pre-defined questions shown to the user
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description The product insights agent element appeared in the visible part of the page
   * @example
   * constructorio.tracker.trackProductInsightsAgentView({
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *     'questions': [
   *        { question: 'Why choose this?' },
   *        { question: 'How is this product made?' },
   *        { question: 'What are the dimensions of this product?' }
   *     ],
   *   },
   * );
   */
  trackProductInsightsAgentView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_view?`;
      const {
        section,
        questions,
        itemId,
        itemName,
        variationId,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        questions,
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent out of view event
   *
   * @function trackProductInsightsAgentOutOfView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description The product insights agent element disappeared from the visible part of the page
   * @example
   * constructorio.tracker.trackProductInsightsAgentOutOfView({
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *   },
   * );
   */
  trackProductInsightsAgentOutOfView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_out_of_view?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent input focus event
   *
   * @function trackProductInsightsAgentFocus
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User focused on the product insights agent input element
   * @example
   * constructorio.tracker.trackProductInsightsAgentFocus({
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *   },
   * );
   */
  trackProductInsightsAgentFocus(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_focus?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent question click event
   *
   * @function trackProductInsightsAgentQuestionClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} parameters.question - Question a user clicked on
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.questionTopic] - Topic category of the question.
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked on a question within the product insights agent
   * @example
   * constructorio.tracker.trackProductInsightsAgentQuestionClick({
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *     'question': 'Why choose this?'
   *   },
   * );
   */
  trackProductInsightsAgentQuestionClick(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_question_click?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        question,
        threadId,
        questionTopic,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
        question,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (questionTopic) {
        bodyParams.question_topic = questionTopic;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent question submit event
   *
   * @function trackProductInsightsAgentQuestionSubmit
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} parameters.question - Question a user submitted
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.questionTopic] - Topic category of the question
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User submitted a question to the product insights agent
   * @example
   * constructorio.tracker.trackProductInsightsAgentQuestionSubmit({
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *     'question': 'Tell me some key highlights about this item?'
   *   },
   * );
   */
  trackProductInsightsAgentQuestionSubmit(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_question_submit?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        question,
        threadId,
        questionTopic,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
        question,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (questionTopic) {
        bodyParams.question_topic = questionTopic;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent answer view event
   *
   * @function trackProductInsightsAgentAnswerView
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} parameters.question - Question a user submitted
   * @param {string} parameters.answerText - Answer text of the question
   * @param {string} [parameters.qnaResultId] - Answer result id returned
   * @param {Array} [parameters.items] - Array of recommended items shown with the answer (max 100)
   * @param {Array} [parameters.followUpQuestions] - Follow-up questions displayed with the answer
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User viewed the answer provided by the product insights agent
   * @example
   * constructorio.tracker.trackProductInsightsAgentAnswerView(
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *     'question': 'Why choose this?',
   *     'answerText': 'This product is awesome!',
   *     'qnaResultId': '0daf0015-fc29-4727-9140-8d5313a1902c',
   *   },
   * );
   */
  trackProductInsightsAgentAnswerView(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_answer_view?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        question,
        answerText,
        qnaResultId,
        items,
        followUpQuestions,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
        question,
        answer_text: answerText,
        qna_result_id: qnaResultId,
      };

      if (items && Array.isArray(items)) {
        bodyParams.items = items.map((item) => helpers.toSnakeCaseKeys(item, false));
      }

      if (followUpQuestions && Array.isArray(followUpQuestions)) {
        bodyParams.follow_up_questions = followUpQuestions.map((q) => helpers.toSnakeCaseKeys(q, false));
      }

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent answer feedback event
   *
   * @function trackProductInsightsAgentAnswerFeedback
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id whose page we are on
   * @param {string} parameters.itemName - Product name whose page we are on
   * @param {string} parameters.feedbackLabel - Feedback value: either "thumbs_up" or "thumbs_down"
   * @param {string} [parameters.qnaResultId] - Answer result id returned
   * @param {string} [parameters.variationId] - Variation id whose page we are on
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description A user provided feedback on an answers usefulness
   * @example
   * constructorio.tracker.trackProductInsightsAgentAnswerFeedback(
   *   {
   *     'itemId': '1',
   *     'itemName': 'item1',
   *     'variationId': '2',
   *     'feedbackLabel': 'thumbs_up',
   *     'qnaResultId': '0daf0015-fc29-4727-9140-8d5313a1902c',
   *   },
   * );
   */
  trackProductInsightsAgentAnswerFeedback(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_answer_feedback?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        feedbackLabel,
        qnaResultId,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
        feedback_label: feedbackLabel,
        qna_result_id: qnaResultId,
      };

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Send product insights agent result click event
   *
   * @function trackProductInsightsAgentResultClick
   * @param {object} parameters - Additional parameters to be sent with request
   * @param {string} parameters.itemId - Product id of the clicked recommendation
   * @param {string} parameters.itemName - Product name of the clicked recommendation
   * @param {string} parameters.question - Question that produced the answer containing this recommendation
   * @param {string} parameters.seedItemId - Product id of the page the PIA widget is on
   * @param {number} [parameters.position] - Position of the clicked item in the list
   * @param {string} [parameters.seedItemName] - Product name of the page the PIA widget is on
   * @param {string} [parameters.seedVariationId] - Variation id of the page the PIA widget is on
   * @param {string} [parameters.qnaResultId] - Answer result id linking the click to a specific answer
   * @param {string} [parameters.variationId] - Variation id of the clicked recommendation
   * @param {string} [parameters.threadId] - Thread ID for grouping events within a conversation
   * @param {string} [parameters.section] - The section name for the item Ex. "Products"
   * @param {object} [networkParameters] - Parameters relevant to the network request
   * @param {number} [networkParameters.timeout] - Request timeout (in milliseconds)
   * @returns {(true|Error)}
   * @description User clicked a recommended product in the PIA widget
   * @example
   * constructorio.tracker.trackProductInsightsAgentResultClick(
   *   {
   *     'itemId': '10',
   *     'itemName': 'rec1',
   *     'question': 'Why choose this?',
   *     'seedItemId': '1',
   *     'position': 1,
   *   },
   * );
   */
  trackProductInsightsAgentResultClick(parameters, networkParameters = {}) {
    // Ensure parameters are provided (required)
    if (parameters && typeof parameters === 'object' && !Array.isArray(parameters)) {
      const baseUrl = `${this.options.serviceUrl}/v2/behavioral_action/product_insights_agent_result_click?`;
      const {
        section,
        itemId,
        itemName,
        variationId,
        position,
        question,
        seedItemId,
        seedItemName,
        seedVariationId,
        qnaResultId,
        threadId,
      } = parameters;
      const queryParams = {};
      const bodyParams = {
        item_id: itemId,
        item_name: itemName,
        variation_id: variationId,
        question,
        seed_item_id: seedItemId,
      };

      if (!helpers.isNil(position)) {
        bodyParams.position = position;
      }

      if (seedItemName) {
        bodyParams.seed_item_name = seedItemName;
      }

      if (seedVariationId) {
        bodyParams.seed_variation_id = seedVariationId;
      }

      if (qnaResultId) {
        bodyParams.qna_result_id = qnaResultId;
      }

      if (threadId) {
        bodyParams.thread_id = threadId;
      }

      if (section) {
        queryParams.section = section;
      }

      const requestURL = `${baseUrl}${applyParamsAsString(queryParams, this.options)}`;
      const requestMethod = 'POST';
      const requestBody = applyParams(bodyParams, {
        ...this.options,
        requestMethod,
      });
      this.requests.queue(
        requestURL,
        requestMethod,
        requestBody,
        networkParameters,
      );
      this.requests.send();
      return true;
    }

    this.requests.send();

    return new Error('parameters is a required parameter of type object');
  }

  /**
   * Subscribe to success or error messages emitted by tracking requests
   *
   * @function on
   * @param {string} messageType - Type of message to listen for ('success' or 'error')
   * @param {function} callback - Callback to be invoked when message received
   * @returns {(true|Error)}
   * @example
   * constructorio.tracker.on('error', (data) => {
   *     // Handle tracking error
   * });
   */
  on(messageType, callback) {
    if (messageType !== 'success' && messageType !== 'error') {
      return new Error('messageType must be a string of value "success" or "error"');
    }

    if (!callback || typeof callback !== 'function') {
      return new Error('callback is required and must be a function');
    }

    this.eventemitter.on(messageType, callback);

    return true;
  }
}

// Exposed for testing
Tracker.RequestQueue = RequestQueue;

module.exports = Tracker;
